import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import { GeneratorShell } from '../components/GeneratorShell';
import {
  generateUuids,
  isValidUuid,
  UUID_NAMESPACES,
  UUID_VERSIONS,
  type UuidNamespacePreset,
  type UuidVersion,
} from '../lib/uuidGen';

const VERSION_LABEL: Record<UuidVersion, string> = {
  v1: 'v1 · 时间戳',
  v4: 'v4 · 随机',
  v5: 'v5 · SHA-1 命名',
  v7: 'v7 · 时间有序',
};

const NAMESPACE_PRESETS: ReadonlyArray<{ id: UuidNamespacePreset; label: string }> = [
  { id: 'DNS', label: 'DNS' },
  { id: 'URL', label: 'URL' },
  { id: 'OID', label: 'OID' },
  { id: 'X500', label: 'X500' },
  { id: 'custom', label: '自定义' },
];

const COUNT_PRESETS = [1, 5, 10, 50, 100, 500, 1000];

/**
 * `/tools/generate/uuid` — UUID v1 / v4 / v5 / v7 生成器。
 *
 * Why v5 sits in a sub-form rather than always-visible inputs: v5 is
 * the niche path; surfacing namespace + name fields for v1/v4/v7 is
 * just visual noise. The toolbar self-collapses to "version pillbox +
 * count select" until the user actually picks v5.
 */
export default function UuidTool(): JSX.Element {
  const [version, setVersion] = useState<UuidVersion>('v4');
  const [count, setCount] = useState<number>(5);
  const [namespacePreset, setNamespacePreset] = useState<UuidNamespacePreset>('DNS');
  const [customNs, setCustomNs] = useState<string>('');
  const [v5Name, setV5Name] = useState<string>('example.com');
  // Bumped to force a regeneration when the user clicks "重新生成".
  const [tick, setTick] = useState(0);

  const namespace =
    namespacePreset === 'custom' ? customNs : UUID_NAMESPACES[namespacePreset];

  const { items, error } = useMemo<{ items: string[]; error?: string }>(() => {
    try {
      const list = generateUuids({
        version,
        count,
        v5: version === 'v5' ? { namespace, name: v5Name } : undefined,
      });
      return { items: list };
    } catch (e) {
      return {
        items: [],
        error: e instanceof Error ? e.message : String(e),
      };
    }
    // `tick` is intentionally part of the dependency list — it's the
    // re-roll lever for non-deterministic versions (v1/v4/v7).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, count, namespace, v5Name, tick]);

  // Auto-regenerate on first paint and on parameter changes.
  useEffect(() => {
    /* derived from useMemo above; no extra work needed */
  }, [items]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="uuid"
        category="generate"
        description="UUID v1 / v4 / v5 / v7 批量生成；v5 支持 DNS/URL/OID/X500 命名空间或自定义 UUID。"
      />

      <GeneratorShell
        controls={
          <UuidControls
            version={version}
            onVersionChange={setVersion}
            count={count}
            onCountChange={setCount}
            namespacePreset={namespacePreset}
            onNamespacePresetChange={setNamespacePreset}
            customNs={customNs}
            onCustomNsChange={setCustomNs}
            v5Name={v5Name}
            onV5NameChange={setV5Name}
          />
        }
        items={items}
        error={error}
        onRegenerate={() => setTick((t) => t + 1)}
      />
    </div>
  );
}

interface UuidControlsProps {
  version: UuidVersion;
  onVersionChange: (v: UuidVersion) => void;
  count: number;
  onCountChange: (n: number) => void;
  namespacePreset: UuidNamespacePreset;
  onNamespacePresetChange: (p: UuidNamespacePreset) => void;
  customNs: string;
  onCustomNsChange: (s: string) => void;
  v5Name: string;
  onV5NameChange: (s: string) => void;
}

function UuidControls(p: UuidControlsProps): JSX.Element {
  const customNsValid =
    p.namespacePreset !== 'custom' || (p.customNs.length > 0 && isValidUuid(p.customNs));

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-1">
        {UUID_VERSIONS.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => p.onVersionChange(v)}
            className={clsx(
              'rounded-md border px-2 py-0.5 text-[11.5px] font-medium transition',
              p.version === v
                ? 'border-accent bg-accent/10 text-accent'
                : 'border-line text-ink-muted hover:border-accent/60 hover:text-accent',
            )}
          >
            {VERSION_LABEL[v]}
          </button>
        ))}
      </div>

      <select
        className="input py-0.5 text-[11.5px]"
        value={p.count}
        onChange={(e) => p.onCountChange(Number(e.target.value))}
        title="生成数量（1–1000）"
      >
        {COUNT_PRESETS.map((c) => (
          <option key={c} value={c}>
            {c} 个
          </option>
        ))}
      </select>

      {p.version === 'v5' && (
        <div className="flex flex-wrap items-center gap-1 rounded-md border border-line/60 bg-bg-soft px-2 py-1">
          <span className="text-[10.5px] uppercase tracking-wide text-ink-dim">
            namespace
          </span>
          <select
            className="input py-0.5 text-[11.5px]"
            value={p.namespacePreset}
            onChange={(e) => p.onNamespacePresetChange(e.target.value as UuidNamespacePreset)}
          >
            {NAMESPACE_PRESETS.map((n) => (
              <option key={n.id} value={n.id}>
                {n.label}
              </option>
            ))}
          </select>
          {p.namespacePreset === 'custom' && (
            <input
              type="text"
              className={clsx(
                'input w-[280px] py-0.5 font-mono text-[11px]',
                p.customNs.length > 0 && !isValidUuid(p.customNs) && 'border-rose-500/60',
              )}
              placeholder="自定义命名空间 UUID（必须合法）"
              value={p.customNs}
              onChange={(e) => p.onCustomNsChange(e.target.value)}
              spellCheck={false}
            />
          )}
          <span className="text-[10.5px] uppercase tracking-wide text-ink-dim">name</span>
          <input
            type="text"
            className="input w-[180px] py-0.5 text-[11.5px]"
            placeholder="非空，例: example.com"
            value={p.v5Name}
            onChange={(e) => p.onV5NameChange(e.target.value)}
            spellCheck={false}
          />
          {!customNsValid && (
            <span className="text-[10.5px] text-rose-600 dark:text-rose-400">
              请输入合法 UUID
            </span>
          )}
        </div>
      )}
    </div>
  );
}
